import React, { useEffect, useState } from "react";
import { useTeamsQuery } from "../../../Services/settingsAPIs/CommonAPI";
import {
  useSlConfigDataQuery,
  useUpdateSlConfigMutation,
} from "../../../Services/general/General";
import {
  useTagsQuery,
  useEditTagsMutation,
} from "../../../Services/customer/Customer";
import { toast } from "react-toastify";
import PriorityBasedInclusionList from "../../../components/settings/ticket/sla/PriorityBasedInclusionList";

/** Only the three mode flags go to `v1/users/team/:id/sl-config/`. */
type SlConfigState = {
  tag_based_sl: boolean;
  disposition_based_sl: boolean;
  workflow_based_sl: boolean;
};

type SlModeKey = "tag_based_sl" | "disposition_based_sl" | "workflow_based_sl";

const SlConfiguration = () => {
  const [selectedTeam, setSelectedTeam] = useState("");
  const [teams, setTeams] = useState([]);
  const [slConfig, setSlConfig] = useState<SlConfigState>({
    tag_based_sl: false,
    disposition_based_sl: false,
    workflow_based_sl: false,
  });
  const [pendingTagId, setPendingTagId] = useState<number | string | null>(
    null,
  );
  /** Collapse inner lists to save vertical space while modes stay on. */
  const [tagSlListExpanded, setTagSlListExpanded] = useState(true);
  const [prioritySlListExpanded, setPrioritySlListExpanded] = useState(true);

  const { data: teamsfetched } = useTeamsQuery({});
  const { data, isLoading } = useSlConfigDataQuery(selectedTeam, {
    skip: !selectedTeam,
  });
  const [triggerUpdateSlConfig] = useUpdateSlConfigMutation();
  const [triggerEditTag] = useEditTagsMutation();

  const { data: tagsList, isLoading: tagsLoading, refetch: refetchTags } =
    useTagsQuery(undefined, {
      skip: !selectedTeam || !slConfig.tag_based_sl,
    });

  useEffect(() => {
    if (teamsfetched) {
      setTeams(teamsfetched);
    }
  }, [teamsfetched]);

  useEffect(() => {
    if (data?.sl_configuration) {
      const sc = data.sl_configuration;
      setSlConfig({
        tag_based_sl: !!sc.tag_based_sl,
        disposition_based_sl: !!sc.disposition_based_sl,
        workflow_based_sl: !!sc.workflow_based_sl,
      });
    }
  }, [data]);

  useEffect(() => {
    setTagSlListExpanded(true);
    setPrioritySlListExpanded(true);
  }, [selectedTeam]);

  const handleTeamChange = (e) => {
    setSelectedTeam(e.target.value);
  };

  const handleToggle = (key: SlModeKey) => {
    setSlConfig((prev) => {
      const next = { ...prev };

      if (key === "tag_based_sl") {
        next.tag_based_sl = !prev.tag_based_sl;
      } else if (key === "disposition_based_sl") {
        next.disposition_based_sl = !prev.disposition_based_sl;
        if (!next.disposition_based_sl) {
          next.workflow_based_sl = false;
        }
      } else if (key === "workflow_based_sl") {
        if (!prev.disposition_based_sl) return prev;
        next.workflow_based_sl = !prev.workflow_based_sl;
      }

      return next;
    });
  };

  const buildSlConfigBody = (cfg: SlConfigState) => ({
    tag_based_sl: cfg.tag_based_sl,
    disposition_based_sl: cfg.disposition_based_sl,
    workflow_based_sl: cfg.workflow_based_sl,
  });

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      const response = await triggerUpdateSlConfig({
        body: buildSlConfigBody(slConfig),
        id: selectedTeam,
      });
      if (response?.data) {
        toast.success("SL Configuration updated successfully!");
      }
    } catch (error) {
      console.error("Error updating SL config:", error);
      toast.error("Failed to update SL configuration. Please try again.");
    }
  };

  /** Tag-based: list from customer tags API; per-tag SL flag via tag update API (same as tag settings). */
  const handleTagSlToggle = async (tag: {
    id: number | string;
    name: string;
    is_sl_enabled?: boolean;
  }) => {
    const next = !Boolean(tag.is_sl_enabled);
    setPendingTagId(tag.id);
    try {
      const response = await triggerEditTag({
        body: {
          name: tag.name,
          is_sl_enabled: next,
        },
        id: tag.id,
      });
      if (response?.data) {
        toast.success(`SL for tag "${tag.name}" ${next ? "enabled" : "disabled"}.`);
        refetchTags();
      } else if ("error" in response && response.error) {
        toast.error("Failed to update tag SL setting.");
      }
    } catch (error) {
      console.error("Error updating tag:", error);
      toast.error("Failed to update tag SL setting.");
    } finally {
      setPendingTagId(null);
    }
  };

  const slItems = [
    {
      key: "tag_based_sl" as const,
      label: "Tag Based SL",
      description:
        "When enabled, SL can be determined by tags. You can use Tag Based SL together with Priority Based SL or Workflow Based SL.",
    },
    {
      key: "disposition_based_sl" as const,
      label: "Priority Based SL",
      description:
        "SL is driven by the ticket's disposition and its priority. Only dispositions you mark below take part.",
    },
    {
      key: "workflow_based_sl" as const,
      label: "Workflow Based SL",
      description:
        "Configure SL by workflow. Turn on Priority Based SL first.",
    },
  ];

  const isLocked = (key: string) => {
    if (key === "workflow_based_sl" && !slConfig.disposition_based_sl)
      return true;
    return false;
  };

  const getLockReason = (key: string) => {
    if (key === "workflow_based_sl" && !slConfig.disposition_based_sl)
      return "Enable Priority Based SL first";
    return "";
  };

  const childBlockStyle: React.CSSProperties = {
    marginLeft: "1rem",
    marginTop: "0.75rem",
    paddingLeft: "1.25rem",
    borderLeft: "3px solid #e4e6ef",
  };

  const renderCollapsibleListHeader = (opts: {
    title: string;
    expanded: boolean;
    onToggle: () => void;
  }) => (
    <button
      type="button"
      className="btn btn-light border w-100 d-flex align-items-center justify-content-between py-2 px-3 mb-2 text-start"
      style={{ borderColor: "#e4e6ef" }}
      aria-expanded={opts.expanded}
      onClick={opts.onToggle}
    >
      <span className="fw-bold text-gray-800 fs-7">{opts.title}</span>
      <i
        className={`bi bi-chevron-${opts.expanded ? "up" : "down"} text-gray-600`}
        aria-hidden
      />
    </button>
  );

  const renderToggleRow = (opts: {
    title: string;
    description?: string;
    locked?: boolean;
    lockReason?: string;
    isOn: boolean;
    onToggle: () => void;
    disabled?: boolean;
  }) => {
    const {
      title,
      description,
      locked,
      lockReason,
      isOn,
      onToggle,
      disabled,
    } = opts;
    return (
      <div
        className="row mb-4 align-items-center p-4 rounded"
        style={{
          border: "1px solid #e4e6ef",
          background: locked ? "#f9f9f9" : isOn ? "#f0f8ff" : "#fff",
          opacity: locked ? 0.65 : 1,
          transition: "background 0.2s, opacity 0.2s",
        }}
      >
        <div className="col">
          <div className="fw-bold text-gray-800 fs-6">{title}</div>
          {(lockReason || description) && (
            <div className="text-muted fs-7 mt-1">
              {lockReason ? (
                <span className="text-warning">
                  <i className="bi bi-lock-fill me-1" />
                  {lockReason}
                </span>
              ) : (
                description
              )}
            </div>
          )}
        </div>
        <div className="col-auto d-flex align-items-center gap-3">
          <span
            className={`badge ${
              isOn ? "badge-light-success" : "badge-light-danger"
            }`}
          >
            {isOn ? "Active" : "Inactive"}
          </span>
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={isOn}
              disabled={locked || disabled}
              onChange={onToggle}
              style={{ cursor: locked || disabled ? "not-allowed" : "pointer" }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="card-toolbar flex-row-fluid justify-content-end gap-5 p-5"
      style={{ marginLeft: "3%", marginRight: "3%" }}
    >
      <div className="row align-items-center" style={{ gap: "10px" }}>
        <div className="row">
          <div className="w-100 mw-200px">
            <label>Select team</label>
            <select
              className="form-select select2-hidden-accessible"
              onChange={handleTeamChange}
              value={selectedTeam}
            >
              <option disabled value="">
                Select team
              </option>
              {teams?.map((team: any) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedTeam && (
        <>
          {isLoading ? (
            <div className="d-flex justify-content-center py-10">
              <span className="spinner-border text-primary" role="status" />
            </div>
          ) : (
            <div className="card-body py-5 mt-5" style={{ overflowY: "auto", maxHeight: "63vh" }}>
              {slItems.map(({ key, label, description }) => {
                const locked = isLocked(key);
                const lockReason = getLockReason(key);
                const isOn = slConfig[key as keyof SlConfigState] as boolean;

                return (
                  <div key={key} className="mb-4">
                    {renderToggleRow({
                      title: label,
                      description,
                      locked,
                      lockReason,
                      isOn,
                      onToggle: () => handleToggle(key),
                    })}

                    {key === "tag_based_sl" && slConfig.tag_based_sl && (
                      <div style={childBlockStyle}>
                        {renderCollapsibleListHeader({
                          title: "Tags included in SL",
                          expanded: tagSlListExpanded,
                          onToggle: () =>
                            setTagSlListExpanded((v) => !v),
                        })}
                        {tagSlListExpanded &&
                          (tagsLoading ? (
                            <div className="d-flex justify-content-center py-4">
                              <span
                                className="spinner-border text-primary spinner-border-sm"
                                role="status"
                              />
                            </div>
                          ) : Array.isArray(tagsList) && tagsList.length > 0 ? (
                            tagsList.map(
                              (tag: {
                                id: number | string;
                                name: string;
                                is_sl_enabled?: boolean;
                              }) => (
                                <div key={tag.id} className="mb-3">
                                  {renderToggleRow({
                                    title: tag.name,
                                    description:
                                      "This tag can apply tag-based SL when active.",
                                    isOn: Boolean(tag.is_sl_enabled),
                                    onToggle: () => handleTagSlToggle(tag),
                                    disabled: pendingTagId === tag.id,
                                  })}
                                </div>
                              ),
                            )
                          ) : (
                            <div className="text-muted py-3 fs-7">
                              No tags found.
                            </div>
                          ))}
                      </div>
                    )}

                    {key === "disposition_based_sl" &&
                      slConfig.disposition_based_sl && (
                        <div style={childBlockStyle}>
                          {renderCollapsibleListHeader({
                            title: "Dispositions for priority based SL",
                            expanded: prioritySlListExpanded,
                            onToggle: () =>
                              setPrioritySlListExpanded((v) => !v),
                          })}
                          {prioritySlListExpanded && (
                            <PriorityBasedInclusionList
                              teamId={String(selectedTeam)}
                            />
                          )}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "right",
              padding: "10px 20px",
            }}
          >
            <button className="btn btn-primary" onClick={submitHandler}>
              Save
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SlConfiguration;
